import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ActivityFeed } from '../../components/portal/ActivityFeed';
import { NeedsAttention } from '../../components/portal/NeedsAttention';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';
interface ProjectsProps {
  /** The New Project form is opened from the header. */
  creating: boolean;
  setCreating: (open: boolean) => void;
}
export function Projects({
  creating,
  setCreating
}: ProjectsProps) {
  const {
    activeProjects,
    archivedProjects,
    templates,
    openProject,
    createProject,
    findExistingProject,
    reopenProject,
    notify
  } = usePortal();
  const [client, setClient] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  /** An existing project with the same client and name, awaiting a decision. */
  const [duplicate, setDuplicate] = useState<{
    id: string;
    client: string;
    name: string;
  } | null>(null);
  function create() {
    createProject({
      client: client.trim(),
      name: name.trim(),
      type: type.trim(),
      templateId
    });
    notify(`${client.trim()} project created`);
    setClient('');
    setName('');
    setType('');
    setTemplateId('');
    setDuplicate(null);
    setCreating(false);
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!client.trim() || !name.trim()) return;

    /* A warning, not a block — Portal may legitimately repeat a name. */
    const existing = findExistingProject(client.trim(), name.trim());
    if (existing && !duplicate) {
      setDuplicate({
        id: existing.id,
        client: existing.client,
        name: existing.name
      });
      return;
    }
    create();
  }

  /* The archive: the same list, kept off the working dashboard. */
  if (showArchive) {
    return <div data-id="element-741" data-mp="3obn4jtpk5vms">
        <button type="button" onClick={() => setShowArchive(false)} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-742" data-mp="3tckl61ux6hm6">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-743" data-mp="6x9ccxo33ar0" />
          Client Projects
        </button>

        <h1 className="mt-10 border-b border-ruleDark pb-8 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-744" data-mp="3aehsnd35y2jm">
          Archived Projects
        </h1>

        <ul className="m-0 list-none p-0" data-id="element-745" data-mp="3kcfexv1gpp9l">
          {archivedProjects.map(row => <li key={row.id} className="grid grid-cols-1 items-baseline gap-x-8 gap-y-4 border-b border-ruleDark py-8 sm:grid-cols-12" data-id="element-746" data-mp="umu7eexihygn">
              <span className="font-display text-2xl leading-none sm:col-span-5" data-id="element-747" data-mp="znhxmydx2b9t">{row.client}</span>
              <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-748" data-mp="2k5iouxut3k36">{row.name}</span>
              <span className="flex flex-wrap items-center gap-4 sm:col-span-3 sm:justify-end" data-id="element-749" data-mp="2f6z06potvu74">
                <button type="button" onClick={() => openProject(row.id)} className={BUTTON_QUIET} data-id="element-750" data-mp="2abtjc5aa95pw">
                  Open Project
                </button>
                <button type="button" onClick={() => {
              reopenProject(row.id);
              notify(`${row.client} restored to Active Projects`);
            }} className={BUTTON_QUIET} data-id="element-751" data-mp="1wed7ufd4qlra">
                  Restore Project
                </button>
              </span>
            </li>)}
          {archivedProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-752" data-mp="3guhxidzaynvw">
              Nothing archived.
            </li>}
        </ul>

        <p className="label-sm mt-8 max-w-xl leading-relaxed text-paper/30" data-id="element-753" data-mp="2iinojri34e4f">
          Nothing is deleted — deliverables, comments, approvals, version history, project
          information and the client link are all retained.
        </p>
      </div>;
  }
  return <div data-id="element-754" data-mp="1usl2bs0uq2dq">
      {creating && <form onSubmit={submit} className="mb-12 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-755" data-mp="1usb8tuglld3a">
          <h2 className="font-display text-2xl leading-none" data-id="element-756" data-mp="1zjt8mf6vx7mo">New Project</h2>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-757" data-mp="1lrvcd4y9v67x">
            <label className="block" data-id="element-758" data-mp="1lps9hkc0tils">
              <span className="label block text-paper/40" data-id="element-759" data-mp="1qqgb0zxvlhu9">Client</span>
              <input value={client} onChange={event => setClient(event.target.value)} placeholder="e.g. Example Group" autoFocus className={FIELD} data-id="element-760" data-mp="1qolnrez4tdn0" />
            </label>
            <label className="block" data-id="element-761" data-mp="x7rwt4m0e69r">
              <span className="label block text-paper/40" data-id="element-762" data-mp="x8ti01wqo957">Project Name</span>
              <input value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Leadership Series" className={FIELD} data-id="element-763" data-mp="t77xbudyqqkg" />
            </label>
            <label className="block" data-id="element-764" data-mp="1g5mes8547s4a">
              <span className="label block text-paper/40" data-id="element-765" data-mp="1g4jlxuo8fflq">Project Type</span>
              <input value={type} onChange={event => setType(event.target.value)} placeholder="e.g. Video Series" className={FIELD} data-id="element-766" data-mp="1c52hn4kjkoi5" />
            </label>
            <label className="block" data-id="element-767" data-mp="1kx4dsqnkvimm">
              <span className="label block text-paper/40" data-id="element-768" data-mp="1ztv6kh2qxw39">Start From (optional)</span>
              <select value={templateId} onChange={event => setTemplateId(event.target.value)} className={SELECT} data-id="element-769" data-mp="1zrhaq608sfun">
                <option value="" className={OPTION} data-id="element-770" data-mp="21zrzjq6io0ns">
                  Blank project
                </option>
                {templates.map(template => <option key={template.id} value={template.id} className={OPTION} data-id="element-771" data-mp="2bvv04rlpzr8m">
                    {template.name}
                  </option>)}
              </select>
            </label>
          </div>
          {duplicate ? <div className="mt-10 max-w-xl border border-paper/30 px-6 py-6" data-id="element-772" data-mp="2lo0qxy08k2m8">
              <p className="font-display text-xl leading-snug" data-id="element-773" data-mp="2plyjnat7t6ud">
                A project with this client and project name already exists.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-774" data-mp="2zm8x8wj5wga2">
                <button type="button" onClick={() => {
            const id = duplicate.id;
            setDuplicate(null);
            setCreating(false);
            openProject(id);
          }} className={BUTTON_SOLID} data-id="element-775" data-mp="2zjcxr3ro1jji">
                  Open Existing Project
                </button>
                <button type="button" onClick={create} className={BUTTON_QUIET} data-id="element-776" data-mp="svm1ycq2rvua">
                  Create Anyway
                </button>
                <button type="button" onClick={() => setDuplicate(null)} className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-777" data-mp="j028dwyzh98p">
                  Cancel
                </button>
              </div>
            </div> : <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-778" data-mp="16naf8jc28qcu">
              <button type="submit" className={BUTTON_SOLID} data-id="element-779" data-mp="11oqpe7uciuf7">
                Create Project
              </button>
              <button type="button" onClick={() => setCreating(false)} className={BUTTON_QUIET} data-id="element-780" data-mp="3rfvrmjkp8ytg">
                Cancel
              </button>
            </div>}
          <p className="label-sm mt-8 max-w-lg leading-relaxed text-paper/30" data-id="element-781" data-mp="fyhj5c1dr031">
            Templates are starting structures only — every deliverable can be added, renamed,
            reordered or removed afterwards.
          </p>
        </form>}

      <NeedsAttention data-id="element-782" data-mp="705liq23j213" />

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-783" data-mp="azecxo9nynth">
        <section aria-labelledby="active-projects" className="lg:col-span-8" data-id="element-784" data-mp="b1hcaywphtav">
          <h2 id="active-projects" className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-785" data-mp="yq0jtc7xm21w">
            Active Projects
          </h2>
          <ul className="m-0 list-none p-0" data-id="element-786" data-mp="pw5eo46t8bm9">
            {activeProjects.map(row => {
            const waiting = row.deliverables.filter(item => item.status === 'Ready for Review' && item.visibleToClient).length;
            const changes = row.deliverables.reduce((total, item) => total + item.changeRequests.filter(request => !request.resolved).length, 0);
            return <li key={row.id} className="border-b border-ruleDark" data-id="element-787" data-mp="3253zd9xw5925">
                  <button type="button" onClick={() => openProject(row.id)} className="group grid w-full grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:grid-cols-12" data-id="element-788" data-mp="3gvkwwi7enetr">
                    <span className="font-display text-3xl leading-none sm:col-span-5 sm:text-4xl" data-id="element-789" data-mp="2o8go0256ygcp">
                      {row.client}
                    </span>
                    <span className="font-display text-xl text-paper/70 sm:col-span-5" data-id="element-790" data-mp="2y5la61z1ebti">
                      {row.name}
                    </span>
                    <span className="label flex items-center justify-between gap-4 text-paper sm:col-span-2 sm:justify-end" data-id="element-791" data-mp="2t7tjtce1ixrf">
                      {changes > 0 ? 'Changes' : waiting > 0 ? 'With client' : projectStatus(row)}
                      <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-792" data-mp="1t0xy0jjui39b" />
                    </span>
                  </button>
                </li>;
          })}
            {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-793" data-mp="19cp5u9iaxx9t">
                No active projects.
              </li>}
          </ul>

          {archivedProjects.length > 0 && <button type="button" onClick={() => setShowArchive(true)} className="label-sm mt-10 text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-794" data-mp="10cajd6tlqc4q">
              Archived Projects · {archivedProjects.length}
            </button>}
        </section>

        <div className="lg:col-span-4 lg:border-l lg:border-ruleDark lg:pl-12" data-id="element-795" data-mp="2xar9ff7feind">
          <ActivityFeed data-id="element-796" data-mp="32918i0w9u3e9" />
        </div>
      </div>
    </div>;
}